'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { BarChart3, Brain, FileBarChart2 } from 'lucide-react';

export default function FeaturesPage() {
  return (
    <div className="min-h-screen flex flex-col font-sans overflow-hidden">
      {/* Background gradient matching inspiration - purple/blue theme */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/50 via-purple-100/30 to-background dark:from-purple-900/20 dark:via-purple-900/5 dark:to-background -z-10"></div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-64 h-64 rounded-full bg-purple-200/20 dark:bg-purple-800/20 blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-[10%] w-96 h-96 rounded-full bg-indigo-200/20 dark:bg-indigo-800/20 blur-3xl -z-10"></div>

      <Navbar />

      <main className="flex-grow pt-24">
        <div className="py-24" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h1
                className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>
                Everything You Need for Data Analysis
              </motion.h1>
              <motion.p
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                Powerful features to help you get the most out of your data.
              </motion.p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" id="features">
              {[
                {
                  icon: <BarChart3 className="h-6 w-6 text-red-500" />,
                  title: 'Charts & Graphs',
                  description: 'Create sleek looking data visualizations and interactive charts.',
                },
                {
                  icon: <Brain className="h-6 w-6 text-blue-500" />,
                  title: 'AI Insights',
                  description: 'Ask anything to your data and get intelligent answers instantly.',
                },
                {
                  icon: <FileBarChart2 className="h-6 w-6 text-green-500" />,
                  title: 'Advanced Analysis',
                  description: 'Perform modeling and predictive forecasting with your data.',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-card/50 backdrop-blur-sm p-6 rounded-xl border border-border hover:bg-card/70 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}>
                  <div className="rounded-lg p-3 bg-muted/50 inline-block mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
